import { useEffect, useMemo, useState } from "react";
import { Loader2, PenLine, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const PRESETS = ["#e11d48", "#f97316", "#eab308", "#22c55e", "#14b8a6", "#0ea5e9", "#6366f1", "#a855f7", "#ec4899", "#78716c"];
const NONE = "__none__";

export function TourneeSelect({ tournees, value, onChange, allowNone = false, noneLabel = "Aucune", testId }) {
  return (
    <Select value={value || NONE} onValueChange={(v) => onChange(v === NONE ? null : v)}>
      <SelectTrigger data-testid={testId}>
        <SelectValue placeholder="Choisir une tournée" />
      </SelectTrigger>
      <SelectContent className="max-h-80">
        {allowNone && <SelectItem value={NONE}>{noneLabel}</SelectItem>}
        {tournees.map((t) => (
          <SelectItem key={t.code} value={t.code}>
            <span className="inline-flex items-center gap-2">
              <span className="w-3 h-3 rounded-sm shrink-0" style={{ background: t.couleur }} />
              <span className="font-mono font-semibold">{t.code}</span>
              <span className="text-muted-foreground truncate max-w-[220px]">{t.libelle}</span>
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/** Création / modification d'une zone : nom, couleur, tournée, SST. */
export function ZoneDialog({ open, zone, tournees, onClose, onSave, onDelete, onEditShape }) {
  const isNew = !zone?.id;
  const [nom, setNom] = useState("");
  const [couleur, setCouleur] = useState(PRESETS[0]);
  const [tournee, setTournee] = useState(null);
  const [sst, setSst] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const ssts = useMemo(() => [...new Set(tournees.map((t) => t.sst).filter(Boolean))].sort(), [tournees]);

  useEffect(() => {
    if (!open) return;
    setNom(zone?.nom || "");
    setCouleur(zone?.couleur || PRESETS[0]);
    setTournee(zone?.tournee || null);
    setSst(zone?.sst || "");
    setNotes(zone?.notes || "");
    setConfirmDelete(false);
    setSaving(false);
  }, [open, zone]);

  const pickTournee = (code) => {
    setTournee(code);
    const t = tournees.find((x) => x.code === code);
    if (t) {
      if (!sst || ssts.includes(sst)) setSst(t.sst || "");
      if (isNew && couleur === PRESETS[0]) setCouleur(t.couleur);
    }
  };

  const current = tournees.find((t) => t.code === tournee);

  const submit = async (e) => {
    e?.preventDefault();
    if (!nom.trim()) return;
    setSaving(true);
    try {
      await onSave({ nom: nom.trim(), couleur, tournee, sst: sst.trim(), notes: notes.trim() });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="zone-dialog">
        <form onSubmit={submit} className="space-y-4">
          <DialogHeader>
            <DialogTitle>{isNew ? "Nouvelle zone" : `Zone « ${zone.nom} »`}</DialogTitle>
            <DialogDescription>
              Toute adresse située dans cette zone sera rattachée à la tournée choisie, en priorité sur le code postal.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-1.5">
            <Label htmlFor="zone-nom">Nom de la zone</Label>
            <Input id="zone-nom" value={nom} onChange={(e) => setNom(e.target.value)} placeholder="ex. Baliste" autoFocus data-testid="zone-nom" />
          </div>

          <div className="space-y-1.5">
            <Label>Code tournée</Label>
            <TourneeSelect tournees={tournees} value={tournee} onChange={pickTournee} allowNone noneLabel="— Sans tournée —" testId="zone-tournee" />
            {current && <p className="text-xs text-muted-foreground">Secteur {current.secteur || "—"} · SST habituelle {current.sst || "—"}</p>}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="zone-sst">SST</Label>
            <Input id="zone-sst" list="zone-sst-list" value={sst} onChange={(e) => setSst(e.target.value)} placeholder="ex. SEM" data-testid="zone-sst" />
            <datalist id="zone-sst-list">{ssts.map((s) => <option key={s} value={s} />)}</datalist>
          </div>

          <div className="space-y-1.5">
            <Label>Couleur</Label>
            <div className="flex flex-wrap items-center gap-2">
              {PRESETS.map((c) => (
                <button
                  type="button"
                  key={c}
                  onClick={() => setCouleur(c)}
                  className={`w-7 h-7 rounded-full border-2 transition-transform ${couleur === c ? "border-foreground scale-110" : "border-transparent"}`}
                  style={{ background: c }}
                  aria-label={`Couleur ${c}`}
                />
              ))}
              {current && (
                <button
                  type="button"
                  onClick={() => setCouleur(current.couleur)}
                  className="text-xs px-2 h-7 rounded-full border border-border hover:bg-accent inline-flex items-center gap-1.5"
                  title="Utiliser la couleur de la tournée"
                >
                  <span className="w-3 h-3 rounded-full" style={{ background: current.couleur }} /> tournée
                </button>
              )}
              <label className="relative w-7 h-7 rounded-full border border-border overflow-hidden cursor-pointer" title="Couleur personnalisée">
                <input type="color" value={couleur} onChange={(e) => setCouleur(e.target.value)} className="absolute -inset-2 w-12 h-12 cursor-pointer" data-testid="zone-couleur" />
              </label>
              <span className="font-mono text-xs text-muted-foreground">{couleur}</span>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="zone-notes">Notes <span className="text-muted-foreground font-normal">(facultatif)</span></Label>
            <Textarea id="zone-notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Consignes, rues limites…" />
          </div>

          <DialogFooter className="gap-2 sm:gap-2 flex-col-reverse sm:flex-row">
            {!isNew && (
              <div className="flex gap-2 sm:mr-auto">
                {confirmDelete ? (
                  <Button type="button" variant="destructive" size="sm" onClick={() => onDelete(zone)} data-testid="zone-delete-confirm">
                    Confirmer la suppression
                  </Button>
                ) : (
                  <Button type="button" variant="outline" size="sm" onClick={() => setConfirmDelete(true)} className="text-destructive">
                    <Trash2 size={14} /> Supprimer
                  </Button>
                )}
                <Button type="button" variant="outline" size="sm" onClick={() => onEditShape(zone)}>
                  <PenLine size={14} /> Tracé
                </Button>
              </div>
            )}
            <Button type="button" variant="ghost" onClick={onClose}>Annuler</Button>
            <Button type="submit" disabled={!nom.trim() || saving} data-testid="zone-save">
              {saving && <Loader2 className="animate-spin" size={14} />} Enregistrer
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
